import { RequirementsV2 } from '@cadstrom/sigma-engine-shared';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpIcon from '@mui/icons-material/Help';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import WarningIcon from '@mui/icons-material/Warning';

type Severity = RequirementsV2.RequirementSeverity;

/** Icon glyph and colour for a single severity. */
export interface SeverityGlyph {
	/** Short human label, surfaced in the icon's tooltip. */
	label: string;
	/** MUI icon component rendered for the severity. */
	Icon: typeof CheckCircleIcon;
	/** Tailwind text-colour class; mirrors the hue `<SeverityPill>` uses for the same severity. */
	colorClass: string;
	/** Tailwind background-colour class for a solid accent strip; the saturated `-9` step of the same hue. */
	accentClass: string;
}

/**
 * Direct, lossless severity → glyph mapping. Every severity gets its own icon so
 * the full granularity survives instead of being collapsed onto the legacy
 * five-value status enum. The three neutral severities (pending, n/a,
 * indeterminate) share the grey hue of `<SeverityPill>` but stay distinguishable
 * by their distinct glyphs.
 */
export const severityGlyph: Record<Severity, SeverityGlyph> = {
	[RequirementsV2.RequirementSeverity.Pass]: {
		label: 'pass',
		Icon: CheckCircleIcon,
		colorClass: 'text-green-a11',
		accentClass: 'bg-green-9',
	},
	[RequirementsV2.RequirementSeverity.Pending]: {
		label: 'pending',
		Icon: HourglassTopIcon,
		colorClass: 'text-gray-a11',
		accentClass: 'bg-gray-7',
	},
	[RequirementsV2.RequirementSeverity.NotApplicable]: {
		label: 'not applicable (N/A)',
		Icon: RemoveCircleIcon,
		colorClass: 'text-gray-a11',
		accentClass: 'bg-gray-7',
	},
	[RequirementsV2.RequirementSeverity.Recommendation]: {
		label: 'recommendation',
		Icon: LightbulbIcon,
		colorClass: 'text-blue-a11',
		accentClass: 'bg-blue-9',
	},
	[RequirementsV2.RequirementSeverity.Indeterminate]: {
		label: 'indeterminate',
		Icon: HelpIcon,
		colorClass: 'text-orange-a11',
		accentClass: 'bg-orange-9',
	},
	[RequirementsV2.RequirementSeverity.Warning]: {
		label: 'warning',
		Icon: WarningIcon,
		colorClass: 'text-amber-a11',
		accentClass: 'bg-amber-9',
	},
	[RequirementsV2.RequirementSeverity.Fail]: {
		label: 'fail',
		Icon: CancelIcon,
		colorClass: 'text-red-a11',
		accentClass: 'bg-red-9',
	},
};
